import {
  ACCEPTED_ALPHANUMERIC_REGEX,
  BENEFICIARY_NAME_REGEX,
  INPUT_MAX_LIMIT,
  INPUT_MIN_LIMIT,
  MAX_AMOUNT,
  NUMBER_REGEX,
  ONLY_DIGITS,
  PHONE_REGEX,
  TEXT_FIELD_VALIDATION_REGEX,
} from "@/shared/common/constants";
import { ErrorMessages } from "@/shared/common/enums";
import type {
  ICommission,
  ValidationSchemaAccountTransferProps,
  ValidationSchemaCommissionProps,
  ValidationSchemaProps,
} from "@/shared/common/types";
import { cleanupAmount, getTotalCommission, isValidAccountChecksum } from "@/shared/helpers";
import * as Yup from "yup";

export const getRequiredStringValidationSchema = ({ t }: ValidationSchemaProps) => Yup
  .string()
  .matches(ACCEPTED_ALPHANUMERIC_REGEX, t(ErrorMessages.WrongSymbols))
  .required(t(ErrorMessages.RequiredField));

export const getTinValidationSchema = ({ t }: ValidationSchemaProps) => Yup
  .string()
  .matches(ONLY_DIGITS, t(ErrorMessages.OnlyDigits))
  .length(8, t(ErrorMessages.TinInvalidLength, { length: 8 }));

export const getSsnValidationSchema = ({ t, schema = Yup }: ValidationSchemaProps) => schema
  .string()
  .matches(ONLY_DIGITS, t(ErrorMessages.OnlyDigits))
  .length(10, t(ErrorMessages.SsnInvalidLength, { length: 10 }))
  .required(t(ErrorMessages.RequiredField));

export const getBooleanValidationSchema = () => Yup.boolean();

export const getAmountValidationSchema = ({ t }: ValidationSchemaProps) => {
  return getRequiredStringValidationSchema({ t })
    .transform(value => cleanupAmount(value, ""))
    .test("maxAmount", t(ErrorMessages.MaxAmount, { count: MAX_AMOUNT }), (value) => {
      if (!value) return true;
      return +value <= MAX_AMOUNT;
    })
    .test("amountValidation", t(ErrorMessages.MinAmount), (value) => +value > 0);
};

export const getCommissionAccountValidationSchema = (
  { t, accountsList }: ValidationSchemaCommissionProps
) => {
  return Yup.string().when("commissionData", {
    is: (commissionData: ICommission) => {
      const total = getTotalCommission(commissionData);
      return total > 0;
    },
    then: () => getRequiredStringValidationSchema({ t })
      .test("balance_check", t(ErrorMessages.NotEnoughBalance), function (value) {
        const { commissionData } = this.parent;
        const total = getTotalCommission(commissionData);
        const balance = accountsList?.find(({ number }) => (number === value))?.expectedBalance || 0;
        return balance > total;
      })
  });
};

export const getCardNumberValidationSchema = ({ t }: ValidationSchemaProps) => {
  return getRequiredStringValidationSchema({ t })
    .transform((val) => val.replace(/\D/g, ""))
    .length(16, t(ErrorMessages.CardNumberMustBe, { count: 16 }));
};

export const getPhoneNumberValidationSchema = ({ t }: ValidationSchemaProps) => {
  return getRequiredStringValidationSchema({ t })
    .transform((value: string | undefined) => {
      return value ? value.replace(/\s+/g, "") : value;
    })
    .matches(PHONE_REGEX, t(ErrorMessages.InvalidPhone));
};

export const getStringInputValidationSchema = ({ t }: ValidationSchemaProps) => {
  return getRequiredStringValidationSchema({ t })
    .test(
      "min_without_spaces",
      t(ErrorMessages.MinInput, { count: INPUT_MIN_LIMIT }),
      (value) => {
        if (!value) return false;
        const lengthWithoutSpaces = value?.replace(/\s/g, "").length;
        return lengthWithoutSpaces >= INPUT_MIN_LIMIT;
      }
    )
    .max(
      INPUT_MAX_LIMIT,
      t(ErrorMessages.MaxInput, { count: INPUT_MAX_LIMIT })
    );
};

export const getPurposeValidationSchema = ({ t }: ValidationSchemaProps) => {
  return getStringInputValidationSchema({ t }).matches(
    TEXT_FIELD_VALIDATION_REGEX,
    t(ErrorMessages.InvalidPurpose)
  );
};

export const getTemplatePurposeValidationSchema = ({ t }: ValidationSchemaProps) => {
  return Yup.string()
    .matches(
      TEXT_FIELD_VALIDATION_REGEX,
      { message: t(ErrorMessages.InvalidPurpose), excludeEmptyString: true }
    )
    .test(
      "min_without_spaces",
      t(ErrorMessages.MinInput, { count: INPUT_MIN_LIMIT }),
      (value) => {
        if (!value) return true;
        const lengthWithoutSpaces = value?.replace(/\s/g, "").length;
        return lengthWithoutSpaces >= INPUT_MIN_LIMIT;
      }
    )
    .max(
      INPUT_MAX_LIMIT,
      t(ErrorMessages.MaxInput, { count: INPUT_MAX_LIMIT })
    ).nullable();
};

export const getTemplateAmountValidationSchema = ({ t }: ValidationSchemaProps) => {
  return Yup.string()
    .transform(value => cleanupAmount(value, ""))
    .test("maxAmount", t(ErrorMessages.MaxAmount, { count: MAX_AMOUNT }), (value) => {
      if (!value) return true;
      return +value <= MAX_AMOUNT;
    })
    .test("minAmount", t(ErrorMessages.MinAmount), (value) => {
      if (!value) return true;
      return +value > 0;
    })
    .nullable();
};

export const getBeneficiaryNameValidationSchema = ({ t }: ValidationSchemaProps) => {
  return Yup.string()
    .required(t(ErrorMessages.RequiredField))
    .matches(BENEFICIARY_NAME_REGEX, t(ErrorMessages.WrongSymbols))
    .test("has-letter", t(ErrorMessages.BeneficiaryNameMustContainLetter), (value) =>
      /\p{L}/u.test(value ?? ""),
    )
    .max(50, t(ErrorMessages.MaxBeneficiaryName, { count: 50 }));
};

export const getTransactionNumberValidationSchema = ({ t }: ValidationSchemaProps) => {
  return getRequiredStringValidationSchema({ t })
    .transform((value: string) => value?.replaceAll(" ", "") || "")
    .min(1, t(ErrorMessages.MinTransactionNumber, { count: 1 }))
    .max(6, t(ErrorMessages.MaxTransactionNumber, { count: 6 }));
};

export const getApplicationNumberValidationSchema = ({ t }: ValidationSchemaProps) => {
  return getRequiredStringValidationSchema({ t })
    .transform((value: string) => value?.replaceAll(" ", "") || "")
    .min(1, t(ErrorMessages.MinApplicationNumber, { count: 1 }))
    .max(6, t(ErrorMessages.MaxApplicationNumber, { count: 6 }));
};

export const getAccountNumberValidationSchema = ({
  t, isAccountNumberInvalid
}: ValidationSchemaAccountTransferProps) => {
  return getRequiredStringValidationSchema({ t })
    .test(
      "account-holder-valid",
      t(ErrorMessages.UnknownAccountHolder),
      () => !isAccountNumberInvalid
    )
    .matches(NUMBER_REGEX, t(ErrorMessages.OnlyNumbers))
    .min(12, t(ErrorMessages.MinAccountNumber, { count: 12 }))
    .max(16, t(ErrorMessages.MaxAccountNumber, { count: 16 }))
    .test(
      "account_checksum",
      t(ErrorMessages.InvalidAccountNumber),
      (value) => {
        if (!value) return true;
        return isValidAccountChecksum(value);
      }
    );
};
